import { ISubscriptionInDTO } from '../../repository/jago/model/input/ISubscriptionInDTO';
import { SubscriptionProvisioningTypeEnum } from '../../repository/jago/model/input/ISubscriptionProvisioningTypeEnum';
import { IEditSubscriptionOutDTO, ISubscriptionOutDTO } from '../../repository/jago/model/output/ISubscriptionOutDTO';
import { isMainTier } from './planner';
import { IPricelistApplyApi, IPricelistApplyResult, IPricelistDiffEntry } from './types';

export function describeError(error: unknown): string {
  const e = error as { response?: { data?: { message?: string } | string; status?: number }; message?: string };
  const data = e && e.response && e.response.data;
  if (data && typeof data === 'object' && data.message) {
    return data.message;
  }
  if (typeof data === 'string' && data) {
    return data;
  }
  if (e && e.message) {
    return e.message;
  }
  return String(error);
}

export function buildCreateDto(entry: IPricelistDiffEntry, mainTierId: number | null): ISubscriptionOutDTO {
  const { row } = entry;
  const dto: ISubscriptionOutDTO = {
    name: row.name,
    sku: row.sku,
    fixedPrice: row.fixedPrice,
    burnRate: row.burnRate,
    logic: row.logic,
    maxLocks: row.maxLocks,
    maxUsers: row.maxUsers,
    maxElements: row.maxElements,
    visible: row.visible,
    resource: row.resource,
    requestToQuote: row.requestToQuote,
    canBeDeactivated: row.canBeDeactivated,
    nextTier: null,
    prevTier: null,
  };
  if (row.category) dto.category = row.category;
  if (row.provisioningType) dto.provisioningType = row.provisioningType;
  if (mainTierId !== null) dto.mainTier = mainTierId;
  return dto;
}

export function buildEditDto(entry: IPricelistDiffEntry, existing: ISubscriptionInDTO): IEditSubscriptionOutDTO {
  const { row } = entry;
  return {
    fixedPrice: row.fixedPrice,
    burnRate: row.burnRate,
    visible: row.visible,
    nextTier: existing.nextTier,
    prevTier: existing.prevTier,
    provisioningType: row.provisioningType || existing.provisioningType || SubscriptionProvisioningTypeEnum.MANUAL,
    requestToQuote: row.requestToQuote,
    canBeDeactivated: row.canBeDeactivated,
  };
}

/**
 * Applies the plan sequentially: DOMAIN creations first (their ids resolve SKU references), then plugin
 * creations, then updates. A failure of one item does not stop the others.
 */
export async function applyPricelistPlan(
  marketplaceId: number,
  entries: IPricelistDiffEntry[],
  api: IPricelistApplyApi,
  onProgress?: (done: number, total: number) => void,
): Promise<IPricelistApplyResult[]> {
  const created = entries.filter(e => e.status === 'NEW');
  const tierCreations = created.filter(e => isMainTier(e.row));
  const pluginCreations = created.filter(e => !isMainTier(e.row));
  const updates = entries.filter(e => e.status === 'CHANGED');
  const total = tierCreations.length + pluginCreations.length + updates.length;
  const results: IPricelistApplyResult[] = [];
  const createdTierIds = new Map<string, number>();
  let done = 0;
  const report = (result: IPricelistApplyResult) => {
    results.push(result);
    done += 1;
    if (onProgress) onProgress(done, total);
  };
  if (onProgress) onProgress(0, total);

  const create = async (entry: IPricelistDiffEntry) => {
    try {
      let mainTierId = entry.mainTierId;
      if (entry.pendingMainTierSku) {
        const resolved = createdTierIds.get(entry.pendingMainTierSku);
        if (resolved === undefined) {
          throw new Error(`Main tier "${entry.pendingMainTierSku}" was not created`);
        }
        mainTierId = resolved;
      }
      const response = await api.createSubscription(marketplaceId, buildCreateDto(entry, mainTierId));
      if (isMainTier(entry.row)) {
        createdTierIds.set(entry.row.sku, response.id);
      }
      report({ entry, outcome: 'CREATED', subscriptionId: response.id });
    } catch (error) {
      report({ entry, outcome: 'FAILED', message: describeError(error) });
    }
  };

  for (const entry of tierCreations) await create(entry);
  for (const entry of pluginCreations) await create(entry);
  for (const entry of updates) {
    try {
      if (!entry.existing) {
        throw new Error('Missing existing subscription');
      }
      await api.editSubscription(marketplaceId, entry.existing.id, buildEditDto(entry, entry.existing));
      report({ entry, outcome: 'UPDATED', subscriptionId: entry.existing.id });
    } catch (error) {
      report({ entry, outcome: 'FAILED', message: describeError(error) });
    }
  }
  return results;
}
